import request, { merchantApi } from '@/config/axios'

/** 高端服务分类：bazi=命理排盘 feng=风水堪舆 qimen=奇门择吉 liuren=六壬占卜 */
export const PREMIUM_CATEGORY_OPTIONS = [
  { label: '命理排盘', value: 'bazi' },
  { label: '风水堪舆', value: 'feng' },
  { label: '奇门择吉', value: 'qimen' },
  { label: '六壬占卜', value: 'liuren' }
]

export const PREMIUM_CATEGORY_LABELS: Record<string, string> = {
  bazi: '命理排盘',
  feng: '风水堪舆',
  qimen: '奇门择吉',
  liuren: '六壬占卜'
}

export const PREMIUM_STATUS_LABELS: Record<number, string> = {
  0: '上架',
  1: '下架'
}

/** 高端订单业务状态：0=待派单 1=服务中 2=已完成 */
export const PREMIUM_ORDER_STATUS_LABELS: Record<number, string> = {
  0: '待派单',
  1: '服务中',
  2: '已完成'
}

/** 高端服务阶段条目 */
export interface PremiumStageVo {
  name: string
  description?: string
  sort?: number
}

/** 高端服务目录 VO（价格单位：分） */
export interface PremiumCatalogVo {
  id?: number
  name: string
  category: string
  intro?: string
  unit: string
  basePrice: number
  priceMin: number
  priceMax: number
  sort?: number
  status?: number
  stages?: PremiumStageVo[]
  createTime?: Date
}

// ==================== 平台端：高端服务目录管理 ====================

export const PremiumCatalogApi = {
  // 查询目录分页（名称关键字/分类/状态筛选；含阶段模板）
  getCatalogPage: (params: { pageNo?: number; pageSize?: number; name?: string; category?: string; status?: number }) => {
    return request.get({ url: '/xuanxue/premium-catalog/page', params })
  },
  // 查询目录详情（含阶段模板）
  getCatalog: (id: number) => {
    return request.get({ url: '/xuanxue/premium-catalog/get', params: { id } })
  },
  // 新建目录（含阶段模板）
  createCatalog: (data: PremiumCatalogVo) => {
    return request.post({ url: '/xuanxue/premium-catalog/create', data })
  },
  // 编辑目录（重设阶段模板；基准价/区间变更即时生效）
  updateCatalog: (data: PremiumCatalogVo) => {
    return request.put({ url: '/xuanxue/premium-catalog/update', data })
  },
  // 目录上下架（下架后机构端隐藏、用户端不展示）
  updateCatalogStatus: (id: number, status: number) => {
    return request.put({ url: '/xuanxue/premium-catalog/update-status', params: { id, status } })
  },
  // 删除目录（有机构配置引用时后端拒绝）
  deleteCatalog: (id: number) => {
    return request.delete({ url: '/xuanxue/premium-catalog/delete', params: { id } })
  }
}

// ==================== 平台端：机构高端订单 / 机构钱包 ====================

/** 机构钱包 VO（价格单位：分） */
export interface InstitutionWalletVo {
  institutionId: number
  institutionName: string
  balance: number
  totalIncome: number
}

export const PremiumAdminApi = {
  // 高端订单分页（全机构；仅已支付有效单；含阶段进度）——订单域接口 /xuanxue/premium/order
  getOrderPage: (params: { institutionId?: number; bizStatus?: number; pageNo?: number; pageSize?: number }) => {
    return request.get({ url: '/xuanxue/premium/order/page', params })
  },
  // 高端订单详情（含阶段进度与报告）
  getOrder: (id: number) => {
    return request.get({ url: '/xuanxue/premium/order/get', params: { id } })
  },
  // 机构钱包分页（机构名称筛选）——钱包域接口 /xuanxue/premium/institution-wallet
  getWalletPage: (params: { name?: string; pageNo?: number; pageSize?: number }) => {
    return request.get({ url: '/xuanxue/premium/institution-wallet/page', params })
  },
  // 某机构钱包流水
  getWalletFlows: (institutionId: number, limit?: number) => {
    return request.get({ url: '/xuanxue/premium/institution-wallet/flows', params: { institutionId, limit } })
  }
}

// ==================== 商户端（机构）：高端服务配置 / 派单 / 钱包 ====================

/** 机构高端服务配置 VO（目录合并视图；价格单位：分） */
export interface InstitutionPremiumConfigVo {
  configId?: number
  catalogId: number
  catalogName: string
  category: string
  intro?: string
  unit: string
  basePrice: number
  price?: number
  status: number
  /** 机构阶段定稿（已配置时） */
  stages?: PremiumStageVo[]
  /** 平台阶段模板（未配置时的默认阶段） */
  templateStages?: PremiumStageVo[]
  priceMin: number
  priceMax: number
}

/** 机构配置保存参数 */
export interface InstitutionPremiumConfigSaveVo {
  configId?: number
  catalogId: number
  price: number
  intro?: string
  status?: number
  stages?: PremiumStageVo[]
}

/** 高端订单 VO（阶段视图；价格单位：分） */
export interface PremiumOrderVo {
  id: number
  orderNo: string
  institutionId: number
  institutionName?: string
  catalogName: string
  serviceIntro?: string
  price: number
  userRemark?: string
  /** 业务状态：0=待派单 1=服务中 2=已完成 */
  bizStatus: number
  tradeStatus?: number
  masterId?: number
  masterName?: string
  createTime?: Date
  payTime?: Date
  assignTime?: Date
  completeTime?: Date
  cancelBy?: number
  cancelReason?: string
  payOrderId?: number
  progress?: number
  stages?: {
    index: number
    name: string
    description?: string
    done: boolean
    reportUrl?: string
    reportFileName?: string
    reportTime?: Date
  }[]
}

export const PremiumMerchantApi = {
  // 我的高端服务配置（目录全量合并，未配置项给目录默认值 + 下架态）
  // 商户端接口（controller.merchant 包 → /merchant-api 前缀，走 merchantApi 商户身份；后端按登录机构过滤）
  getMyConfigs: () => {
    return merchantApi.get({ url: '/merchant-api/xuanxue/premium/merchant/my-configs' })
  },
  // 保存服务配置（上架定价/调价/阶段定稿一次提交）
  saveConfig: (data: InstitutionPremiumConfigSaveVo) => {
    return merchantApi.post({ url: '/merchant-api/xuanxue/premium/merchant/save-config', data })
  },
  // 配置上下架
  updateConfigStatus: (configId: number, status: number) => {
    return merchantApi.put({ url: '/merchant-api/xuanxue/premium/merchant/update-status', params: { configId, status } })
  },
  // 高端订单分页（本机构）
  getOrderPage: (params: { status?: number; pageNo?: number; pageSize?: number }) => {
    return merchantApi.get({ url: '/merchant-api/xuanxue/premium/merchant/order-page', params })
  },
  // 高端订单详情（含阶段进度与报告）
  getOrder: (id: number) => {
    return merchantApi.get({ url: '/merchant-api/xuanxue/premium/merchant/order', params: { id } })
  },
  // 派单（派给本机构挂靠师傅；阶段定稿快照进订单）
  assign: (data: { tradeOrderId: number; masterId: number }) => {
    return merchantApi.post({ url: '/merchant-api/xuanxue/premium/merchant/assign', data })
  },
  // 机构钱包
  getWallet: () => {
    return merchantApi.get({ url: '/merchant-api/xuanxue/premium/merchant/wallet' })
  },
  // 钱包流水
  getWalletFlows: (limit?: number) => {
    return merchantApi.get({ url: '/merchant-api/xuanxue/premium/merchant/wallet-flows', params: { limit } })
  }
}
